import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactElement;
  allowRoles?: string[];
  requirePermissions?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowRoles, requirePermissions }) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-text">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role-based access control for specific routes
  if (allowRoles && allowRoles.length > 0) {
    if (!user) {
      console.error('[ProtectedRoute] No user object found!');
      // Redirect to dashboard if no user
      return <Navigate to="/" replace />;
    }
    
    // Debug: Log user object to verify role is set
    console.log('[ProtectedRoute] Checking role access:', {
      userId: user.id,
      userEmail: user.email,
      userName: user.name,
      userRole: user.role,
      userRoleType: typeof user.role,
      allowRoles,
      fullUserObject: user
    });
    
    // Check if user has any of the allowed roles
    // Use lenient matching to handle role variations (e.g., "super-admin", "Super Admin", "SUPER-ADMIN")
    const userRoleRaw = user.role;
    const userRoleLower = typeof userRoleRaw === 'string' ? userRoleRaw.toLowerCase().trim() : '';
    const userRoleNormalized = userRoleLower.replace(/[\s_-]/g, '-');
    
    console.log('[ProtectedRoute] Role processing:', {
      userRoleRaw,
      userRoleLower,
      userRoleNormalized,
      allowRoles
    });
    
    // Check if user role matches any of the allowed roles (lenient check)
    const hasAllowedRole = allowRoles.some(allowedRole => {
      const allowedRoleLower = allowedRole.toLowerCase().trim();
      const allowedRoleNormalized = allowedRoleLower.replace(/[\s_-]/g, '-');
      
      // Exact match
      if (userRoleRaw === allowedRole) {
        console.log('[ProtectedRoute] Exact match:', { userRoleRaw, allowedRole });
        return true;
      }
      
      // Normalized match (handles "super-admin" vs "Super Admin")
      if (userRoleNormalized === allowedRoleNormalized) {
        console.log('[ProtectedRoute] Normalized match:', { userRoleNormalized, allowedRoleNormalized });
        return true;
      }
      
      // Contains match for super-admin (handles "super-admin", "Super Admin", etc.)
      // This is a very lenient check - if both contain "super" and "admin", allow access
      if (allowedRoleLower.includes('super') && allowedRoleLower.includes('admin')) {
        const userHasSuperAndAdmin = userRoleLower.includes('super') && userRoleLower.includes('admin');
        if (userHasSuperAndAdmin) {
          console.log('[ProtectedRoute] Contains match (super-admin):', { userRoleLower, allowedRoleLower });
          return true;
        }
      }
      
      // Contains match (very lenient) - check if user role contains allowed role or vice versa
      if (userRoleLower.includes(allowedRoleLower) || allowedRoleLower.includes(userRoleLower)) {
        console.log('[ProtectedRoute] Contains match (general):', { userRoleLower, allowedRoleLower });
        return true;
      }
      
      return false;
    });
    
    if (!hasAllowedRole) {
      console.warn('[ProtectedRoute] Access denied - User role does not match allowed roles:', {
        userRole: userRoleRaw,
        userRoleLower,
        userRoleNormalized,
        allowRoles,
        hasAllowedRole
      });
      // Redirect to dashboard if user doesn't have required role
      return <Navigate to="/" replace />;
    }
    
    console.log('[ProtectedRoute] Access granted - User role matches allowed roles:', {
      userRole: userRoleRaw,
      userRoleLower,
      userRoleNormalized,
      allowRoles,
      hasAllowedRole
    });
  }

  // Permission-based access control
  if (requirePermissions && requirePermissions.length > 0 && user) {
    // Check if user has at least one of the required permissions (OR logic)
    const userPermissions = user.permissions || [];
    const userPermissionsSet = new Set(userPermissions);
    
    // Check if user has at least one of the required permissions
    const userHasPermission = requirePermissions.some(perm => userPermissionsSet.has(perm));
    
    // Special case: Super-admin has all permissions
    const userRoleRaw = user.role;
    const userRoleLower = typeof userRoleRaw === 'string' ? userRoleRaw.toLowerCase().trim() : '';
    const userRoleNormalized = userRoleLower.replace(/[\s_-]/g, '-');
    const isSuperAdmin = user && (
      userRoleNormalized === 'super-admin' || 
      userRoleLower === 'superadmin' ||
      userRoleLower === 'super admin' ||
      userRoleRaw === 'super-admin' ||
      userRoleRaw === 'Super Admin' ||
      userRoleRaw === 'SUPER-ADMIN' ||
      userRoleRaw === 'SUPER_ADMIN' ||
      (typeof userRoleRaw === 'string' && userRoleRaw.toLowerCase().includes('super') && userRoleRaw.toLowerCase().includes('admin'))
    );
    
    // Super-admin bypasses permission checks
    if (isSuperAdmin) {
      console.log('[ProtectedRoute] Access granted - Super-admin bypasses permission check');
      return children;
    }
    
    if (!userHasPermission) {
      console.warn('[ProtectedRoute] Access denied - Insufficient permissions:', {
        userId: user.id,
        userEmail: user.email,
        userRole: user.role,
        userPermissions: userPermissions,
        requiredPermissions: requirePermissions,
        hasPermission: userHasPermission
      });
      return (
        <div className="min-h-screen flex items-center justify-center bg-background text-text">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold text-text mb-4">Access Denied</h1>
            <p className="text-muted-foreground mb-4">You don't have the required permissions to access this page.</p>
            <p className="text-sm text-muted-foreground mb-4">Required permissions: {requirePermissions.join(', ')}</p>
            <p className="text-xs text-muted-foreground">Your permissions: {userPermissions.join(', ') || 'None'}</p>
            <Navigate to="/" replace />
          </div>
        </div>
      );
    }
    
    console.log('[ProtectedRoute] Access granted - User has required permissions:', {
      userId: user.id,
      userEmail: user.email,
      requiredPermissions: requirePermissions,
      hasPermission: userHasPermission
    });
  }

  return children;
};

export default ProtectedRoute;


